import React, { Component } from "react";
import PropTypes from "prop-types";
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import { applyDutchLang } from './geomanPatches';
import { createBaseLayer, createOverlays } from './baseLayers';
import { VLAANDEREN_CENTER, DEFAULT_ZOOM, MIN_ZOOM, BOUNDS_PADDING, OSM_ATTRIBUTION, FeatureNames, legacyPointIcon, markerDivIcon } from './MapConstants';
import { MapLoader, Legende, MapManual } from './MapOverlays';
import { createDirectionDecorator } from './directionDecorator';
import Button from '../Button';

require("./Map.css");

const manualButtonStyle = {
    zIndex: '1000',
    position: 'absolute',
    bottom: '22px',
    left: '10px',
    paddingLeft: '15px',
    paddingRight: '15px',
    pointerEvents: 'auto'
};

const legendeButtonStyle = {
    zIndex: '1000',
    position: 'absolute',
    top: '10px',
    right: '10px',
    pointerEvents: 'auto'
};

// pointer-events: none zodat de kaart onder deze laag bedienbaar blijft; interactieve elementen zetten zelf 'auto'.
// Font spiegelt .leaflet-container, anders erven de panelen de (grotere) paginafont.
const overlayLayerStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    pointerEvents: 'none',
    fontFamily: '"Helvetica Neue", Arial, Helvetica, sans-serif',
    fontSize: '0.75rem',
    lineHeight: 1.5
};

class MapView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            legendeVisible: false,
            manualVisible: false
        };
        this.containerRef = React.createRef();
        this.map = null;
        this.geoJsonGroup = null;
        this.markerGroup = null;
    }

    componentDidMount() {
        const props = this.props;

        this.map = L.map(this.containerRef.current, {
            center: props.center || VLAANDEREN_CENTER,
            zoom: props.zoom || DEFAULT_ZOOM,
            minZoom: MIN_ZOOM,
            zoomControl: false,
            attributionControl: false
        });

        L.control.attribution({ position: 'bottomleft' }).addTo(this.map);
        createBaseLayer(props.baseLayerUrl, props.baseLayerAttribution).addTo(this.map);

        const overlaysByName = {};
        createOverlays().forEach(overlay => {
            overlaysByName[overlay.name] = overlay.layer;
            if (overlay.checked)
                overlay.layer.addTo(this.map);
        });
        L.control.layers(null, overlaysByName, { position: 'topleft' }).addTo(this.map);

        L.control.scale({ metric: true, imperial: false, updateWhenIdle: true, position: 'bottomright' }).addTo(this.map);
        L.control.zoom({ position: 'bottomright' }).addTo(this.map);

        applyDutchLang(this.map);

        this.geoJsonGroup = L.layerGroup().addTo(this.map);
        this.markerGroup = L.layerGroup().addTo(this.map);
        this.renderGeoJsonLayers();
        this.renderMarkers();

        this.fitView();

        if (props.onMapReady)
            props.onMapReady(this.map);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.hidden && !this.props.hidden)
            this.map.invalidateSize();

        const hasBounds = this.props.bounds && this.props.bounds.length;
        if (this.boundsSignature(prevProps.bounds) !== this.boundsSignature(this.props.bounds) && !this.props.hidden)
            this.fitView();
        else if (this.viewSignature(prevProps) !== this.viewSignature(this.props) && !this.props.hidden && !hasBounds)
            this.map.setView(this.props.center || VLAANDEREN_CENTER, this.props.zoom || DEFAULT_ZOOM);

        if (this.geoJsonSignatureChanged(prevProps.geoJsonLayers, this.props.geoJsonLayers))
            this.renderGeoJsonLayers();

        if (this.markersSignature(prevProps) !== this.markersSignature(this.props))
            this.renderMarkers();
    }

    componentWillUnmount() {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    boundsSignature(bounds) {
        if (!bounds || !bounds.length)
            return 'none';
        return `${bounds.length}|${JSON.stringify(bounds[0])}|${JSON.stringify(bounds[bounds.length - 1])}`;
    }

    viewSignature(props) {
        return JSON.stringify([props.center, props.zoom]);
    }

    markersSignature(props) {
        const markers = (props.markers || []).filter(Boolean);
        return JSON.stringify([markers.map(m => [m.id, m.lat, m.long, m.color, m.size, m.tooltip]), props.markersDraggable]);
    }

    geoJsonSignatureChanged(prevLayers, layers) {
        const previous = prevLayers || [];
        const current = layers || [];
        if (previous.length !== current.length)
            return true;
        return current.some((entry, i) => previous[i].id !== entry.id || previous[i].data !== entry.data);
    }

    fitView() {
        const { bounds, center, zoom } = this.props;
        if (bounds && bounds.length)
            this.map.fitBounds(bounds, { padding: BOUNDS_PADDING });
        else
            this.map.setView(center || VLAANDEREN_CENTER, zoom || DEFAULT_ZOOM);
    }

    renderGeoJsonLayers() {
        this.geoJsonGroup.clearLayers();
        (this.props.geoJsonLayers || []).forEach(entry => {
            if (!entry.data)
                return;
            const geoJsonLayer = L.geoJSON(entry.data, {
                style: entry.style,
                onEachFeature: entry.onEachFeature,
                pointToLayer: entry.pointToLayer || ((feature, latlng) => L.marker(latlng, { icon: legacyPointIcon }))
            });
            this.geoJsonGroup.addLayer(geoJsonLayer);

            if (entry.showDirection) {
                geoJsonLayer.getLayers().forEach(layer => {
                    // rijrichting is route-exclusief: bevoorradingszones staan in oudere GPX als LineStrings en zouden anders ook pijlen krijgen
                    const name = layer.feature && layer.feature.properties && layer.feature.properties.name;
                    if (name === FeatureNames.BEVOORRADINGSZONE)
                        return;
                    if (layer instanceof L.Polyline && !(layer instanceof L.Polygon))
                        this.geoJsonGroup.addLayer(createDirectionDecorator(layer, layer.options.color));
                });
            }
        });
    }

    renderMarkers() {
        this.markerGroup.clearLayers();
        const { markers, markersDraggable, onMarkerDragEnd } = this.props;
        (markers || []).filter(Boolean).forEach(m => {
            const marker = L.marker([m.lat, m.long], {
                icon: markerDivIcon(m.size, m.color),
                draggable: !!markersDraggable
            });
            if (m.tooltip)
                marker.bindTooltip(String(m.tooltip));
            if (onMarkerDragEnd)
                marker.on('dragend', (e) => onMarkerDragEnd(e, m.id));
            this.markerGroup.addLayer(marker);
        });
    }

    onLegendeHover = () => this.setState({ legendeVisible: true });
    onLegendeUnhover = () => this.setState({ legendeVisible: false });
    onManualHover = () => this.setState({ manualVisible: true });
    onManualUnhover = () => this.setState({ manualVisible: false });

    render() {
        const { hidden, isLoading, legendeRows, manualContent, mapName, children } = this.props;
        const { legendeVisible, manualVisible } = this.state;

        const mapStyle = hidden
            ? { width: '100%', height: '600px', display: 'none' }
            : { width: '100%', height: '600px' };

        return (
            <div style={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
                <div id={mapName} ref={this.containerRef} style={mapStyle} />
                {!hidden &&
                    <div style={overlayLayerStyle}>
                        {isLoading ? <MapLoader /> : null}
                        {legendeRows && legendeRows.length ?
                            <Button small style={legendeButtonStyle} className="leaflet-control-layers leaflet-control" text=""
                                children={<div style={{ color: '#000' }}>Legende</div>}
                                onMouseOver={this.onLegendeHover} />
                            : null}
                        {legendeVisible && legendeRows && legendeRows.length ?
                            <Legende onMouseOut={this.onLegendeUnhover} customRows={legendeRows} /> : null}
                        {manualContent ?
                            <Button small style={manualButtonStyle} className="leaflet-control-layers leaflet-control" text=""
                                children={<div style={{ color: '#000' }}>Info <i className="fa fa-info-circle" style={{ color: 'red' }} /></div>}
                                onMouseOver={this.onManualHover} />
                            : null}
                        {manualVisible && manualContent ?
                            <MapManual onMouseOut={this.onManualUnhover}>{manualContent}</MapManual> : null}
                        {children}
                    </div>}
            </div>
        );
    }
}

MapView.propTypes = {
    mapName: PropTypes.string,
    baseLayerUrl: PropTypes.string.isRequired,
    baseLayerAttribution: PropTypes.string,
    center: PropTypes.array,
    zoom: PropTypes.number,
    bounds: PropTypes.array,
    hidden: PropTypes.bool,
    isLoading: PropTypes.bool,
    legendeRows: PropTypes.array,
    manualContent: PropTypes.node,
    geoJsonLayers: PropTypes.array,
    markers: PropTypes.array,
    markersDraggable: PropTypes.bool,
    onMarkerDragEnd: PropTypes.func,
    onMapReady: PropTypes.func,
    children: PropTypes.node
};

MapView.defaultProps = {
    baseLayerAttribution: OSM_ATTRIBUTION,
    markersDraggable: true
};

export default MapView;
